import { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { LOCALES, type Locale } from '@nk/shared';
import { LocaleProvider, useLocale } from '@/i18n/locale-context';
import { createQueryClient } from '@/lib/query';
import { seedQueryClient, type RouteData } from '@/lib/route-data';
import { setDocumentLang } from '@/lib/dom';
import { AppShell } from '@/components/shell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ErrorState, ToastProvider } from '@/components/ui';
import ArticlePage from '@/routes/ArticlePage';
import AuthorPage from '@/routes/AuthorPage';
import CategoryPage from '@/routes/CategoryPage';
import DistrictPage from '@/routes/DistrictPage';
import Home from '@/routes/Home';
import LiveEventPage from '@/routes/LiveEventPage';
import LivePage from '@/routes/LivePage';
import NewsletterPage from '@/routes/NewsletterPage';
import NotFound from '@/routes/NotFound';
import SearchPage from '@/routes/SearchPage';
import StaticPage from '@/routes/StaticPage';
import TagPage from '@/routes/TagPage';
import TrendingPage from '@/routes/TrendingPage';
import VideoPage from '@/routes/VideoPage';
import VideosPage from '@/routes/VideosPage';

// Separate chunks, client-only: neither ships to a reader. See src/admin/AdminApp.tsx.
const AdminApp = lazy(() => import('@/admin/AdminApp'));
const KitchenSink = lazy(() => import('@/routes/KitchenSink'));

/**
 * The public route table — supports seamless client-side switching between all supported locales.
 * Order is load-bearing: static routes and specific shapes precede general slug captures.
 */
function PublicRoutes() {
  return (
    <Routes>
      {LOCALES.map((locale) => {
        const base = `/${locale}`;
        return (
          <Fragment key={locale}>
            <Route path={base} element={<Home />} />

            {/* Static routes first — every one of these words is in RESERVED_SLUGS. */}
            <Route path={`${base}/search`} element={<SearchPage />} />
            <Route path={`${base}/trending`} element={<TrendingPage />} />
            <Route path={`${base}/newsletter`} element={<NewsletterPage />} />
            <Route path={`${base}/videos`} element={<VideosPage />} />
            <Route path={`${base}/videos/:slug`} element={<VideoPage />} />
            <Route path={`${base}/live`} element={<LivePage />} />
            <Route path={`${base}/live/:slug`} element={<LiveEventPage />} />
            <Route path={`${base}/local`} element={<DistrictPage />} />
            <Route path={`${base}/local/:districtSlug`} element={<DistrictPage />} />
            <Route path={`${base}/tag/:slug`} element={<TagPage />} />
            <Route path={`${base}/author/:slug`} element={<AuthorPage />} />

            <Route path={`${base}/about`} element={<StaticPage titleKey="footer.about" />} />
            <Route path={`${base}/contact`} element={<StaticPage titleKey="footer.contact" />} />
            <Route path={`${base}/privacy`} element={<StaticPage titleKey="footer.privacy" />} />
            <Route path={`${base}/terms`} element={<StaticPage titleKey="footer.terms" />} />
            <Route
              path={`${base}/advertise`}
              element={<StaticPage titleKey="footer.advertise" />}
            />

            <Route
              path={`${base}/dev/kitchen-sink`}
              element={
                <Suspense fallback={null}>
                  <KitchenSink />
                </Suspense>
              }
            />

            {/* Content routes last, longest first (district form before plain one). */}
            <Route path={`${base}/:categorySlug/:districtSlug/:slug`} element={<ArticlePage />} />
            <Route path={`${base}/:categorySlug/:slug`} element={<ArticlePage />} />
            <Route path={`${base}/:categorySlug`} element={<CategoryPage />} />
          </Fragment>
        );
      })}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/** The boundary's fallback needs the locale, so it lives inside `LocaleProvider`. */
function ShellWithBoundary() {
  const { t } = useLocale();

  return (
    <AppShell>
      <ErrorBoundary
        fallback={(reset) => (
          <div className="px-gutter py-16">
            <ErrorState
              title={t('error.genericTitle')}
              body={t('error.genericBody')}
              retryLabel={t('action.retry')}
              onRetry={reset}
            />
          </div>
        )}
      >
        <PublicRoutes />
      </ErrorBoundary>
    </AppShell>
  );
}

export default function App({
  locale: initialLocale,
  initialData = {},
}: {
  locale: Locale;
  initialData?: RouteData;
}) {
  const location = useLocation();
  const currentLocale: Locale = location.pathname.startsWith('/en')
    ? 'en'
    : location.pathname.startsWith('/as')
      ? 'as'
      : initialLocale;

  useEffect(() => {
    setDocumentLang(currentLocale);
  }, [currentLocale]);

  const [queryClient] = useState(() => {
    const client = createQueryClient();
    seedQueryClient(client, initialData);
    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider locale={currentLocale}>
        <ToastProvider>
          <Routes>
            {/* The admin tree renders outside the public shell — it has its own. */}
            {LOCALES.map((locale) => (
              <Route
                key={locale}
                path={`/${locale}/admin/*`}
                element={
                  <Suspense fallback={null}>
                    <AdminApp />
                  </Suspense>
                }
              />
            ))}
            <Route
              path="/admin/*"
              element={
                <Suspense fallback={null}>
                  <AdminApp />
                </Suspense>
              }
            />
            <Route path="*" element={<ShellWithBoundary />} />
          </Routes>
        </ToastProvider>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
